/**
 * Web agent status: the twin of `HealthAgentStatus.tsx`.
 *
 * The line under the score card while the server works on the CV (design
 * `.cvh-working .cvh-agent-bar`): the 22 px working heart, what the agent is
 * doing (14/500, ink) and one optional muted context line (13 px). One
 * polite live region; a new label fades in over 180 ms (Web Animations API,
 * skipped under `prefers-reduced-motion`). Logical properties only.
 */
import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { HealthHeart } from './HealthHeart';
import type { HealthAgentStatusProps } from './health-props';

export type { HealthAgentStatusProps };

/** Design `.cvh-working .cvh-agent-status svg { width: 22px }`. */
const HEART_SIZE = 22;
const FADE_MS = 180;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Fades the element in whenever `text` changes (not on the first paint). */
function useFadeOnChange<T extends HTMLElement>(text: string | null | undefined) {
  const ref = useRef<T>(null);
  const first = useRef(true);
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const node = ref.current;
    if (!node || !text || prefersReducedMotion() || typeof node.animate !== 'function') return;
    const fade = node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: FADE_MS,
      easing: 'ease-out',
      fill: 'backwards',
    });
    return () => fade.cancel();
  }, [text]);
  return ref;
}

export function HealthAgentStatus({ label, context, working }: HealthAgentStatusProps) {
  const labelRef = useFadeOnChange<HTMLSpanElement>(label);
  const contextRef = useFadeOnChange<HTMLParagraphElement>(context);

  return (
    <div
      className="tabbio-health tabbio-health-agent"
      style={rootStyle}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-working={working ? 'true' : 'false'}
    >
      <div style={rowStyle}>
        <span style={heartStyle} aria-hidden="true">
          <HealthHeart size={HEART_SIZE} state={working ? 'working' : 'resting'} />
        </span>
        <span ref={labelRef} style={labelStyle}>
          {label}
        </span>
      </div>
      {context ? (
        <p ref={contextRef} style={contextStyle}>
          {context}
        </p>
      ) : null}
    </div>
  );
}

// Design `.cvh-agent-bar { min-height: 40px; margin: 4px 0 12px }`.
const rootStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBlock: '4px 12px',
  minWidth: 0,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 40,
  minWidth: 0,
};

const heartStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  width: HEART_SIZE,
  height: HEART_SIZE,
};

const labelStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  lineHeight: '21px',
  fontWeight: 500,
  color: 'var(--health-ink)',
  textAlign: 'start',
};

// Under the label text, not the heart: heart (22) + gap (10).
const contextStyle: CSSProperties = {
  margin: 0,
  marginInlineStart: HEART_SIZE + 10,
  fontSize: 13,
  lineHeight: '20px',
  color: 'var(--health-ink-muted)',
  textAlign: 'start',
};

export default HealthAgentStatus;
